import React, { useState, useEffect, useCallback } from 'react';
import { cleanProceedingName } from '../lib/cleanProceedingName';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertCircle, Loader, RefreshCw, Info } from 'lucide-react';

type Period = 'day' | 'week' | 'month';

interface TrendingDocket {
  rank: number;
  docket_number: string;
  filing_count: number;
  proceeding_name: string | null;
}

interface TrendingResponse {
  period: Period;
  start_date: string;
  end_date: string;
  dockets: TrendingDocket[];
  filings_scanned?: number;
  sample_cap?: number;
  scan_complete?: boolean;
  hit_sample_cap?: boolean;
  ranking_method?: string;
  cached?: boolean;
}

const PERIOD_LABELS: Record<Period, string> = {
  day: 'Last 24 Hours',
  week: 'Last 7 Days',
  /** Matches backend: same calendar date last month through today (not a rolling 30-day window). */
  month: 'Past month',
};

/** Best-effort sentence case for FCC all-caps proceeding titles. */
function toTrendingTitle(name: string | null): string {
  if (name == null || name.trim() === '') return '—';
  const t = cleanProceedingName(name);
  if (t === '') return '—';
  if (t.length > 3 && t === t.toUpperCase()) {
    return t.charAt(0) + t.slice(1).toLowerCase();
  }
  return t;
}

function formatTrendingRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  try {
    const a = new Date(start).toLocaleDateString('en-US', opts);
    const b = new Date(end).toLocaleDateString('en-US', opts);
    return `${a} – ${b}`;
  } catch {
    return `${start} – ${end}`;
  }
}

/** When true, per-docket integers are a full census for the date window; otherwise they are sample hits. */
function areFilingCountsExact(data: TrendingResponse | null): boolean {
  return data?.scan_complete === true;
}

function countColumnHeaderTitle(exact: boolean): string {
  return exact
    ? 'Filings on this docket in the selected date range (full ECFS scan for this period).'
    : 'How often this docket appeared in our FCC-wide sample—not necessarily every filing on that docket for the period. Open a proceeding for full totals.';
}

function podiumBarPct(count: number, topFilerCount: number): number {
  if (topFilerCount <= 0) return 0;
  return Math.min(100, Math.round((count / topFilerCount) * 100));
}

/** Activity column: width vs max filing count among ranks 4+ only (never vs #1). API sorts by count desc, so rank 4 is rest[0] and matches max in this slice. */
function tableBarPct(count: number, maxInRank4PlusGroup: number): number {
  const n = Number(count) || 0;
  const max = Number(maxInRank4PlusGroup) || 1;
  if (max <= 0) return 0;
  const raw = (n / max) * 100;
  const pct = Math.min(100, Math.round(raw));
  if (n > 0 && pct < 2) return 2;
  return pct;
}

const ACTIVITY_TRACK = '#e7e5e4';
/** Solid gradient avoids nested %-width children (often collapse in grid). */
function activityBarStyle(pct: number): React.CSSProperties {
  const p = Math.max(0, Math.min(100, pct));
  return {
    background: `linear-gradient(90deg, var(--cg-amber, #d97706) 0%, var(--cg-amber, #d97706) ${p}%, ${ACTIVITY_TRACK} ${p}%, ${ACTIVITY_TRACK} 100%)`,
  };
}

const PODIUM_RANK_CLASS: Record<1 | 2 | 3, { card: string; rank: string; bar: string }> = {
  1: {
    card: 'border-amber-500 border',
    rank: 'text-amber-500',
    bar: 'bg-amber-500',
  },
  2: {
    card: 'border-cg-border',
    rank: 'text-slate-400',
    bar: 'bg-slate-400',
  },
  3: {
    card: 'border-cg-border',
    rank: 'text-amber-800',
    bar: 'bg-orange-700',
  },
};

export const TrendingDocketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');
  const [data, setData] = useState<TrendingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrending = useCallback(async (p: Period) => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-trending-dockets?period=${p}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const result: TrendingResponse = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trending dockets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrending(period);
  }, [period, fetchTrending]);

  const handleDocketClick = (docketNumber: string) => {
    navigate(`/proceedings/${encodeURIComponent(docketNumber)}`);
  };

  /** Podium bars only: relative to #1's filing count. */
  const topFilerCount = data?.dockets[0]?.filing_count ?? 0;
  const top3 = data?.dockets.slice(0, 3) ?? [];
  const rest = data?.dockets.slice(3) ?? [];

  const maxFilingsAmongRank4Plus =
    rest.length === 0
      ? 1
      : Math.max(...rest.map((d) => Number(d.filing_count) || 0), 1);

  const countsExact = areFilingCountsExact(data);
  const countColumnLabel = countsExact ? 'Filings' : 'Sample hits';

  const activateRow = (docketNumber: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDocketClick(docketNumber);
    }
  };

  return (
    <div className="w-full py-6 sm:py-10">
      <div className="mx-auto w-full max-w-[1100px]">
        <header className="mb-9">
          <p className="mb-3 flex items-center gap-2.5 text-[11px] font-medium uppercase tracking-[0.1em] text-cg-amber">
            <span className="relative inline-flex h-3 w-3 shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-40" />
              <span className="relative h-2 w-2 rounded-full bg-cg-amber shadow-[0_0_10px_rgba(217,119,6,0.65)]" />
            </span>
            Live activity
          </p>
          <h1 className="font-display text-[clamp(1.875rem,3vw+1rem,2.375rem)] font-normal leading-tight tracking-tight text-cg-ink">
            Trending Dockets
          </h1>
          <p className="mt-2 text-sm font-light text-neutral-500">
            Most active FCC dockets by recent activity
            {data && !loading && (
              <>
                {' · '}
                {formatTrendingRange(data.start_date, data.end_date)}
                {' · '}
                Top {data.dockets.length} in this period
              </>
            )}
            .
          </p>
        </header>

        <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
          <div className="flex rounded-lg bg-[#eeeee9] p-0.5">
            {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`rounded-md px-4 py-1.5 text-[13px] transition-all duration-150 ${
                  period === p
                    ? 'bg-white font-medium text-cg-ink shadow-sm'
                    : 'font-normal text-neutral-500 hover:text-cg-ink'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
          {data && !loading && (
            <div className="flex items-center gap-2 text-xs font-light text-neutral-400">
              <span>Top {data.dockets.length} dockets in this period</span>
              <button
                type="button"
                onClick={() => fetchTrending(period)}
                className="rounded p-1 text-neutral-400 transition-colors hover:bg-white hover:text-cg-amber"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {data && !loading && (
          <p
            className={`mb-5 flex flex-wrap items-center gap-1.5 text-[11px] font-light leading-snug ${
              data.scan_complete === true
                ? 'text-emerald-800/90'
                : data.hit_sample_cap === true
                  ? 'text-amber-900/85'
                  : 'text-neutral-500'
            }`}
            role="status"
            title={
              data.hit_sample_cap === true
                ? 'Rankings use a FCC-wide sample (not every filing received agency-wide). The number beside each docket is how often it appeared in that sample—not the full filing count for that docket. Open a proceeding for authoritative totals.'
                : data.scan_complete !== true && data.hit_sample_cap !== true
                  ? 'Per-docket figures may be based on a limited FCC-wide sample. Open a proceeding for full filing totals.'
                  : undefined
            }
          >
            <Info className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            {data.scan_complete === true && (
              <>
                Full ECFS scan
                {data.filings_scanned != null && data.filings_scanned > 0
                  ? ` · ${data.filings_scanned.toLocaleString()} filings in range`
                  : ''}
                .
              </>
            )}
            {data.hit_sample_cap === true && (
              <>
                Sampled up to {(data.sample_cap ?? 5000).toLocaleString()} FCC-wide filings—not full per-docket
                counts.
              </>
            )}
            {data.scan_complete !== true && data.hit_sample_cap !== true && (
              <>Row counts may be sample-based—open a proceeding for full totals.</>
            )}
          </p>
        )}

        {error && (
          <div className="mb-6 rounded-xl border border-cg-border bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div>
                <h3 className="mb-1 text-sm font-semibold text-red-800">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-cg-border bg-white py-16 shadow-sm">
            <Loader className="h-8 w-8 animate-spin text-cg-amber" />
            <p className="text-sm font-light text-neutral-600">
              Scanning filings from the FCC ECFS API&hellip; this may take a moment.
            </p>
          </div>
        )}

        {!loading && data && data.dockets.length > 0 && (
          <>
            <div className="mb-7 grid grid-cols-1 gap-3 md:grid-cols-3">
              {top3.map((docket) => {
                const r = docket.rank as 1 | 2 | 3;
                const styles = PODIUM_RANK_CLASS[r] ?? PODIUM_RANK_CLASS[3];
                const barW = podiumBarPct(docket.filing_count, topFilerCount);
                return (
                  <button
                    key={docket.docket_number}
                    type="button"
                    onClick={() => handleDocketClick(docket.docket_number)}
                    className={`relative overflow-hidden rounded-xl border border-cg-border bg-white p-5 text-left shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-neutral-400 ${styles.card}`}
                  >
                    <div className={`font-display text-[52px] font-normal leading-none tracking-tight ${styles.rank}`}>
                      #{docket.rank}
                    </div>
                    <div className="mb-1.5 mt-3 text-xs font-medium uppercase tracking-[0.05em] text-neutral-400">
                      {docket.docket_number}
                    </div>
                    <div className="mb-3.5 min-h-[2.5rem] text-[13px] font-normal leading-snug text-cg-ink">
                      {toTrendingTitle(docket.proceeding_name)}
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-medium tabular-nums tracking-tight text-cg-ink">
                        {docket.filing_count.toLocaleString()}
                      </span>
                      <span className="text-xs font-light text-neutral-400">
                        {countsExact ? 'filings' : 'sample hits'}
                      </span>
                    </div>
                    <div className="mt-3.5 h-0.5 overflow-hidden rounded-sm bg-neutral-100">
                      <div className={`h-full rounded-sm transition-all duration-300 ${styles.bar}`} style={{ width: `${barW}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>

            {rest.length > 0 && (
              <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                  <div
                    className="grid grid-cols-[40px_minmax(72px,90px)_1fr_72px_120px] gap-0 border-b border-cg-border px-4 pb-2"
                    role="row"
                  >
                    <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-neutral-400">#</span>
                    <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-neutral-400">Docket</span>
                    <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-neutral-400">
                      Proceeding
                    </span>
                    <span
                      className="flex items-center justify-end gap-1 text-right text-[11px] font-medium uppercase tracking-[0.06em] text-neutral-400"
                      title={countColumnHeaderTitle(countsExact)}
                    >
                      {countColumnLabel}
                      <Info className="h-3 w-3 shrink-0 text-neutral-400" aria-hidden />
                    </span>
                    <span className="text-right text-[11px] font-medium uppercase tracking-[0.06em] text-neutral-400">
                      Activity
                    </span>
                  </div>
                  {rest.map((docket) => {
                    const barW = tableBarPct(
                      Number(docket.filing_count) || 0,
                      maxFilingsAmongRank4Plus,
                    );
                    return (
                      <div
                        key={docket.docket_number}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleDocketClick(docket.docket_number)}
                        onKeyDown={(e) => activateRow(docket.docket_number, e)}
                        className="grid cursor-pointer grid-cols-[40px_minmax(72px,90px)_1fr_72px_120px] items-center gap-0 rounded-md border-b border-neutral-200/80 px-4 py-3 transition-colors hover:bg-white"
                      >
                        <div className="text-[13px] font-medium text-neutral-300 tabular-nums">{docket.rank}</div>
                        <div className="text-xs font-medium tracking-wide text-neutral-500 tabular-nums">
                          {docket.docket_number}
                        </div>
                        <div className="truncate pr-5 text-[13px] font-normal text-cg-ink" title={toTrendingTitle(docket.proceeding_name)}>
                          {toTrendingTitle(docket.proceeding_name)}
                        </div>
                        <div
                          className="text-right text-[13px] font-medium tabular-nums text-cg-ink"
                          title={countsExact ? undefined : countColumnHeaderTitle(false)}
                        >
                          {docket.filing_count.toLocaleString()}
                        </div>
                        <div className="w-full min-w-[100px] pl-2">
                          <div
                            className="h-2 w-full rounded-sm transition-[background] duration-300"
                            style={activityBarStyle(barW)}
                            aria-hidden
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {!loading && !error && data && data.dockets.length === 0 && (
          <div className="rounded-xl border border-cg-border bg-white py-16 text-center shadow-sm">
            <TrendingUp className="mx-auto mb-4 h-14 w-14 text-neutral-300" />
            <p className="text-lg font-light text-neutral-500">No filings found for this period.</p>
          </div>
        )}
      </div>
    </div>
  );
};
